import { FC, useMemo } from 'react';
import {
  Box,
  Chip,
  IconButton,
  InputBase,
  type Theme,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { PlusIcon, XIcon } from '@phosphor-icons/react';

import { asTableRows, coerceScalar } from '../../utils/fieldValue';
import { editBoxSx } from '../../utils/tokens';

import type { FieldInputProps } from './fieldInput.types';

const headSx = {
  px: 1.5,
  py: 1,
  textAlign: 'start',
  typography: 'textXS_bold',
  color: 'text.tertiary',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  // Allow long headers to wrap within their fixed-width column instead of
  // forcing the column wider.
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  borderBottom: (theme: Theme) => `1px solid ${theme.palette.grey[300]}`,
  backgroundColor: 'grey.200',
} as const;

const cellSx = {
  px: 1.5,
  py: 1,
  typography: 'textXS_regular',
  color: 'text.primary',
  borderBottom: (theme: Theme) => `1px solid ${theme.palette.grey[300]}`,
  verticalAlign: 'top',
  // Fixed layout gives every column an equal share; long values wrap instead
  // of stretching their column.
  wordBreak: 'break-word',
} as const;

/** Fixed width of the trailing remove-row action column (edit mode only). */
const ACTION_COL_WIDTH = 44;

/**
 * Column types whose cell value is an array (`string_list`, `number_list`,
 * `multi_select`). These are edited as comma-separated text and stored as an
 * array — the backend rejects a bare string for such a column ("expected
 * array"). Kept in sync with the list types dispatched in FieldRenderer.
 */
const LIST_COLUMN_TYPES = new Set([
  'multi_select',
  'string_list',
  'number_list',
  'list',
]);

/** Parse a comma-separated edit string into a typed list-column cell value. */
function parseListCell(colType: string, raw: string): (string | number)[] {
  const parts = raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (colType === 'number_list') {
    return parts.map(Number).filter((n) => Number.isFinite(n));
  }
  return parts;
}

/** Text shown in a cell's edit input: lists join with ', ', scalars stringify. */
function cellEditText(value: unknown): string {
  if (Array.isArray(value)) return value.join(', ');
  return value == null ? '' : String(value);
}

/** Vertical separator between columns (logical, so it flips under RTL). */
const colDivider = {
  borderInlineStart: (theme: Theme) => `1px solid ${theme.palette.grey[300]}`,
} as const;

/** Render a single read-only cell: arrays → chips, null → em dash, else text. */
const ReadCell: FC<{ value: unknown }> = observer(({ value }) => {
  if (value == null || value === '') return <>—</>;
  if (Array.isArray(value)) {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {value.map((v, i) => (
          <Chip
            key={`${String(v)}-${i}`}
            label={String(v)}
            size="small"
            sx={{
              height: 20,
              typography: 'textXS_regular',
              backgroundColor: 'grey.200',
              color: 'text.primary',
            }}
          />
        ))}
      </Box>
    );
  }
  return <>{String(value)}</>;
});

/**
 * Renderer for a table field (array of row objects). The catalog ships the
 * column schema as the field's `example` (a sample row whose keys, in order,
 * are the columns), so columns are taken from there first and then extended
 * with any extra keys that happen to appear in the stored data. Using the
 * example as the authority keeps headers correct, ordered, and stable even
 * when an extracted row is partial or its keys are in a different order.
 *
 * Read mode shows a static grid; edit mode allows per-cell editing plus add/
 * remove row. "Add row" is always available when editing — on a table with no
 * example and no rows it bootstraps a single column so it is never a dead end.
 */
export const TableGridField: FC<FieldInputProps> = observer(
  ({ field, value, mode, dirty, disabled, onChange }) => {
    const { t } = useTrans();
    const rows = useMemo(() => asTableRows(value), [value]);
    const canEdit = mode === 'edit' && !disabled;
    const empty = rows.length === 0;

    const columns = field.columns ?? [];

    const updateCell = (rowIdx: number, col: string, raw: string) => {
      // Coerce per the column's own declared type. List columns (`string_list`
      // etc.) parse comma-separated text into an array; every other type is a
      // scalar — otherwise a manually-typed number stays a string and fails the
      // field's `z.number()` validation, and a list column saves a bare string
      // and fails the backend's "expected array" check.
      const colType = columns.find((c) => c.key === col)?.type ?? 'string';
      const next = LIST_COLUMN_TYPES.has(colType)
        ? parseListCell(colType, raw)
        : coerceScalar(colType, raw);
      onChange?.(rows.map((r, i) => (i === rowIdx ? { ...r, [col]: next } : r)));
    };

    const addRow = () => {
      const blank: Record<string, unknown> = {};
      if (columns.length > 0) {
        // Seed list columns with `[]` (not `''`) so an untouched list cell
        // saves as an empty array rather than a string the backend rejects.
        for (const c of columns)
          blank[c.key] = LIST_COLUMN_TYPES.has(c.type) ? [] : '';
      } else {
        blank.value = '';
      }
      onChange?.([...rows, blank]);
    };

    const removeRow = (rowIdx: number) =>
      onChange?.(rows.filter((_, i) => i !== rowIdx));

    const colSpan = Math.max(columns.length, 1) + (canEdit ? 1 : 0);

    return (
      <Box sx={{ ...editBoxSx(mode === 'edit' ? dirty : false), overflow: 'hidden' }}>
        <Box sx={{ overflowX: 'auto', backgroundColor: 'background.paper' }}>
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              typography: 'textXS_regular',
              // Fixed layout so every column gets an equal, consistent width
              // (aligned across all field tables), matching the hub table.
              tableLayout: 'fixed',
            }}
          >
            {columns.length > 0 && (
              <colgroup>
                {columns.map((c) => (
                  <col
                    key={c.key}
                    style={{
                      width: canEdit
                        ? `calc((100% - ${ACTION_COL_WIDTH}px) / ${columns.length})`
                        : `${100 / columns.length}%`,
                    }}
                  />
                ))}
                {canEdit && <col style={{ width: ACTION_COL_WIDTH }} />}
              </colgroup>
            )}
            <Box component="thead">
              <Box component="tr">
                {columns.map((c, colIdx) => (
                  <Box
                    component="th"
                    key={c.key}
                    sx={{
                      ...headSx,
                      ...(colIdx > 0 ? colDivider : null),
                    }}
                  >
                    {c.label ?? c.key}
                  </Box>
                ))}
                {canEdit && (
                  <Box
                    component="th"
                    sx={{ ...headSx, ...colDivider, width: 36 }}
                  />
                )}
              </Box>
            </Box>
            <Box component="tbody">
              {empty && (
                <Box component="tr">
                  <Box
                    component="td"
                    colSpan={colSpan}
                    sx={{
                      ...cellSx,
                      textAlign: 'center',
                      color: 'text.disabled',
                      fontStyle: 'italic',
                      py: 2.75,
                    }}
                  >
                    {t('app.orgProfile.field.noRows')}
                  </Box>
                </Box>
              )}
              {rows.map((row, rowIdx) => (
                <Box component="tr" key={rowIdx}>
                  {columns.map((col, colIdx) => (
                    <Box
                      component="td"
                      key={col.key}
                      sx={{ ...cellSx, ...(colIdx > 0 ? colDivider : null) }}
                    >
                      {canEdit ? (
                        <InputBase
                          fullWidth
                          value={cellEditText(row[col.key])}
                          onChange={(e) => updateCell(rowIdx, col.key, e.target.value)}
                          sx={{ typography: 'textXS_regular', p: 0 }}
                        />
                      ) : (
                        <ReadCell value={row[col.key]} />
                      )}
                    </Box>
                  ))}
                  {canEdit && (
                    <Box
                      component="td"
                      sx={{ ...cellSx, ...colDivider, textAlign: 'center' }}
                    >
                      <IconButton
                        size="small"
                        aria-label={t('app.orgProfile.field.removeRow')}
                        onClick={() => removeRow(rowIdx)}
                      >
                        <XIcon size={13} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {canEdit && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 1,
              borderTop: (theme) => `1px solid ${theme.palette.grey[300]}`,
              backgroundColor: 'grey.200',
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={addRow}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                border: 0,
                background: 'transparent',
                cursor: 'pointer',
                typography: 'textXS_semiBold',
                color: 'text.secondary',
                fontFamily: 'inherit',
              }}
            >
              <PlusIcon size={12} /> {t('app.orgProfile.field.addRow')}
            </Box>
            <Typography variant="textXS_regular" color="text.disabled">
              {rows.length}
            </Typography>
          </Box>
        )}
      </Box>
    );
  },
);
